import { useNavigate } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import SiteNavbar from '@/components/feature/SiteNavbar';
import SiteFooter from '@/components/feature/SiteFooter';
import ScrollToTop from '@/components/feature/ScrollToTop';
import RevealWrapper from '@/pages/home/components/RevealWrapper';
import gSoftLogoDark from '@/pages/images/g-soft-logo-beyaz2.png';
import defterKasaLogo from '@/pages/images/defter-kasa-logo.png';
import { getProductProgress } from '@/config/productProgress';
import { useProductProgressMap } from '@/context/ProductProgressContext';
import { formatDownloadCount } from '@/config/productStats';
import { useProductStatsMap } from '@/context/ProductStatsContext';
import { PRODUCT_CARD_THEMES, type ProductCardTheme, type ProductThemeKey } from '@/config/productCardTheme';

type AppStatus = 'LIVE' | 'DEV' | 'SOON';

interface AppItem {
  id: string;
  productKey: ProductThemeKey;
  nameKey: string;
  categoryKey: string;
  status: AppStatus;
  version: string;
  descKey: string;
  longDescKey: string;
  image: string;
  route?: string;
  externalLink?: string;
  windowsLink?: string;
  tags: string[];
  stats: { labelKey: string; valueKey?: string; metric?: 'downloads' | 'rating' }[];
  progress: number;
  progressLabelKey: string;
}

const APPS: AppItem[] = [
  {
    id: '01',
    productKey: 'defter-kasa',
    nameKey: 'apps.dk.name',
    categoryKey: 'app.defterkasa.category',
    status: 'LIVE',
    version: 'a1.0.1',
    descKey: 'apps.dk.description',
    longDescKey: 'apps.dk.longDescription',
    image: defterKasaLogo,
    route: '/defter-kasa',
    externalLink: 'https://play.google.com/store/apps/details?id=com.companyname.kucukesnafcari&pcampaignid=web_share',
    tags: [
      'apps.dk.tag.offlineFirst',
      'apps.dk.tag.invoiceGeneration',
      'apps.dk.tag.expenseTracking',
      'apps.dk.tag.pdfExport',
      'apps.dk.tag.customerManagement',
      'apps.dk.tag.multiCurrency',
    ],
    stats: [
      { labelKey: 'apps.dk.stat.downloads', metric: 'downloads' },
      { labelKey: 'apps.dk.stat.rating', metric: 'rating' },
      { labelKey: 'apps.dk.stat.version', valueKey: 'apps.dk.value.version' },
      { labelKey: 'apps.dk.stat.size', valueKey: 'apps.dk.value.size' },
    ],
    progress: getProductProgress('defter-kasa'),
    progressLabelKey: 'apps.dk.progressLabel',
  },
  {
    id: '02',
    productKey: 'kozmik-gunluk',
    nameKey: 'apps.kg.name',
    categoryKey: 'app.kozmik.category',
    status: 'DEV',
    version: 'v0',
    descKey: 'apps.kg.description',
    longDescKey: 'apps.kg.longDescription',
    image: gSoftLogoDark,
    route: '/kozmik-gunluk',
    tags: [
      'apps.kg.tag.dailyJournaling',
      'apps.kg.tag.moodTracking',
      'apps.kg.tag.cosmicThemes',
      'apps.kg.tag.encryptedEntries',
      'apps.kg.tag.streakTracking',
      'apps.kg.tag.darkUi',
    ],
    stats: [
      { labelKey: 'apps.kg.stat.downloads', metric: 'downloads' },
      { labelKey: 'apps.kg.stat.status', valueKey: 'apps.kg.value.status' },
      { labelKey: 'apps.kg.stat.platform', valueKey: 'common.platform.android' },
      { labelKey: 'apps.kg.stat.version', valueKey: 'apps.kg.value.version' },
      { labelKey: 'apps.kg.stat.release', valueKey: 'apps.kg.value.release' },
    ],
    progress: getProductProgress('kozmik-gunluk'),
    progressLabelKey: 'apps.kg.progressLabel',
  },
];

function MiniProgressBar({ progress, label, tone }: { progress: number; label: string; tone: ProductCardTheme }) {
  const { t } = useTranslation();
  const [displayed, setDisplayed] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setStarted(true); },
      { threshold: 0.4 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!started) return;
    let frame: number;
    const startTime = performance.now();
    const duration = 1200;
    const animate = (now: number) => {
      const elapsed = now - startTime;
      const tt = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - tt, 3);
      setDisplayed(Math.round(eased * progress));
      if (tt < 1) frame = requestAnimationFrame(animate);
    };
    frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, [started, progress]);

  return (
    <div ref={ref} className="mt-6">
      <div className="flex items-center justify-between mb-1.5">
        <span className={`font-mono text-[10px] tracking-widest uppercase ${tone.progressLabel}`}>{t('apps.devProgress')}</span>
        <div className="flex items-center gap-2">
          <span className={`font-mono text-[10px] tracking-widest ${tone.progressLabel}`}>{label}</span>
          <span className={`font-mono text-sm font-black tabular-nums ${tone.progressValue}`}>
            {displayed}<span className={`text-xs ${tone.progressLabel}`}>%</span>
          </span>
        </div>
      </div>
      <div className={`relative h-px w-full overflow-hidden ${tone.progressTrack}`}>
        <div
          className={`absolute left-0 top-0 h-full ${tone.progressFill}`}
          style={{
            width: started ? `${progress}%` : '0%',
            transition: started ? 'width 1.2s cubic-bezier(0.16,1,0.3,1)' : 'none',
          }}
        />
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: AppStatus }) {
  const { t } = useTranslation();
  if (status === 'LIVE') {
    return (
      <span className="font-mono text-xs px-2.5 py-1 border border-white/30 text-white bg-white/5 tracking-widest">
        {t('products.status.live')}
      </span>
    );
  }
  if (status === 'DEV') {
    return (
      <span className="font-mono text-xs px-2.5 py-1 border border-white/10 text-neutral-500 tracking-widest">
        {t('products.status.dev')}
      </span>
    );
  }
  return (
    <span className="font-mono text-xs px-2.5 py-1 border border-white/8 text-neutral-600 tracking-widest">
      {t('products.status.soon')}
    </span>
  );
}

export default function AppsPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { progress: liveProgress } = useProductProgressMap();
  const { stats: liveStats } = useProductStatsMap();

  const APPS_LINKS = [
    { label: t('nav.home'),    href: '/',      isRoute: true },
    { label: t('nav.apps'),    href: '/apps',  isRoute: true },
    { label: t('nav.games'),   href: '/games', isRoute: true },
    { label: t('nav.contact'), href: '#contact' },
  ];

  return (
    <div className="min-h-screen bg-[#0c0c0c]">
      <SiteNavbar links={APPS_LINKS} sectionAware={false} />

      {/* Page Header */}
      <section className="pt-32 pb-16 px-6 lg:px-14 border-b border-white/5">
        <RevealWrapper direction="up" threshold={0.1}>
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8">
            <div>
              <p className="font-mono text-xs text-neutral-600 tracking-widest uppercase mb-4">{t('apps.label')}</p>
              <h1 className="text-5xl lg:text-7xl font-black text-white tracking-tight leading-none mb-4">{t('apps.title')}</h1>
              <p className="text-neutral-500 text-sm leading-relaxed max-w-lg">{t('apps.description')}</p>
            </div>
            <div className="flex items-center gap-6">
              <div className="text-right">
                <p className="font-mono text-3xl font-black text-white">{APPS.length}</p>
                <p className="font-mono text-xs text-neutral-600 tracking-widest uppercase">{t('apps.total')}</p>
              </div>
              <div className="w-px h-12 bg-white/10" />
              <div className="text-right">
                <p className="font-mono text-3xl font-black text-white">{APPS.filter(a => a.status === 'LIVE').length}</p>
                <p className="font-mono text-xs text-neutral-600 tracking-widest uppercase">{t('apps.live')}</p>
              </div>
            </div>
          </div>
        </RevealWrapper>
      </section>

      {/* Apps List */}
      <section>
        {APPS.map((app, idx) => {
          const theme = PRODUCT_CARD_THEMES[app.productKey];
          return (
          <RevealWrapper key={app.id} direction="up" delay={idx * 100} threshold={0.1}>
            <div className={`border-b ${theme.rowBorder} ${theme.rowHover} transition-colors duration-300 ${idx % 2 === 1 ? theme.rowAlt : ''}`}>
              <div className="px-6 lg:px-14 py-12 lg:py-16">
                <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
                  {/* Left: Number + Icon */}
                  <div className="lg:col-span-1 flex lg:flex-col items-center lg:items-start gap-4">
                    <span className="font-black text-4xl text-white/5 leading-none">{app.id}</span>
                    <div className={`w-14 h-14 border flex items-center justify-center ${theme.iconBox}`}>
                      <img src={app.image} alt={t(app.nameKey)} className="w-10 h-10 object-contain" />
                    </div>
                  </div>

                  {/* Center: Info */}
                  <div className="lg:col-span-7">
                    <div className="flex flex-wrap items-center gap-3 mb-2">
                      <h2 className="text-2xl lg:text-4xl font-black text-white tracking-tight">{t(app.nameKey)}</h2>
                      <StatusBadge status={app.status} />
                    </div>
                    <p className={`font-mono text-xs tracking-wider mb-5 ${theme.meta}`}>
                      {t(app.categoryKey)} · {app.version}
                    </p>
                    <p className="text-neutral-300 text-sm leading-relaxed mb-3">{t(app.descKey)}</p>
                    <p className="text-neutral-600 text-sm leading-relaxed max-w-xl mb-6">{t(app.longDescKey)}</p>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-2 mb-8">
                      {app.tags.map((tagKey) => (
                        <span
                          key={tagKey}
                          className={`font-mono text-xs border px-3 py-1.5 transition-colors duration-200 ${theme.tag}`}
                        >
                          {t(tagKey)}
                        </span>
                      ))}
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                      {app.stats
                        .filter((s) => s.metric !== 'downloads' || liveStats[app.productKey].showDownloadsInApps)
                        .map((s) => (
                        <div key={s.labelKey} className={`border px-4 py-3 ${theme.statBox}`}>
                          <p className={`font-mono text-base font-black mb-1 ${theme.statValue}`}>
                            {s.metric === 'downloads'
                              ? formatDownloadCount(liveStats[app.productKey].downloads)
                              : s.metric === 'rating'
                                ? liveStats[app.productKey].rating
                                : t(s.valueKey ?? '')}
                          </p>
                          <p className={`font-mono text-[10px] tracking-widest uppercase ${theme.statLabel}`}>{t(s.labelKey)}</p>
                        </div>
                      ))}
                    </div>

                    <MiniProgressBar progress={liveProgress[app.productKey]} label={t(app.progressLabelKey)} tone={theme} />
                  </div>

                  {/* Right: Actions */}
                  <div className="lg:col-span-4 flex lg:justify-end items-start">
                    <div className="flex flex-col gap-2 w-full lg:w-auto">
                      {app.route && (
                        <button
                          onClick={() => navigate(app.route!)}
                          className="group/btn inline-flex w-full justify-between items-center gap-3 px-6 py-4 border border-white/20 text-white font-mono text-xs tracking-widest uppercase hover:bg-white hover:text-black transition-all duration-300 sm:whitespace-nowrap cursor-pointer"
                        >
                          {t('products.viewDetails')}
                          <i className="ri-arrow-right-line text-base group-hover/btn:translate-x-1 transition-transform duration-200" />
                        </button>
                      )}
                      {app.externalLink && (
                        <a
                          href={app.externalLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex w-full justify-between items-center gap-2 px-6 py-3 text-neutral-600 font-mono text-xs tracking-widest uppercase hover:text-neutral-400 transition-colors duration-200 sm:whitespace-nowrap"
                        >
                          <i className="ri-google-play-line" />
                          {t('products.googlePlay')}
                          <i className="ri-arrow-right-up-line text-xs" />
                        </a>
                      )}
                      {app.windowsLink && (
                        <a
                          href={app.windowsLink}
                          className="inline-flex w-full justify-between items-center gap-2 px-6 py-3 text-neutral-600 font-mono text-xs tracking-widest uppercase hover:text-neutral-400 transition-colors duration-200 sm:whitespace-nowrap"
                        >
                          <i className="ri-windows-line" />
                          {t('dk.cta.windows')}
                          <i className="ri-arrow-right-up-line text-xs" />
                        </a>
                      )}
                      {!app.externalLink && app.status !== 'LIVE' && (
                        <span className="inline-flex w-full justify-between items-center gap-2 px-6 py-3 text-neutral-700 font-mono text-xs tracking-widest uppercase sm:whitespace-nowrap">
                          <i className="ri-time-line" />
                          {t('apps.comingSoon')}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </RevealWrapper>
          );
        })}
      </section>

      {/* Bottom CTA */}
      <RevealWrapper direction="up" threshold={0.1}>
        <div className="px-6 lg:px-14 py-16 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div>
            <p className="font-mono text-xs text-neutral-600 tracking-widest uppercase mb-2">{t('apps.exploreMore')}</p>
            <p className="text-neutral-400 text-sm">{t('apps.exploreDesc')}</p>
          </div>
          <button
            onClick={() => navigate('/games')}
            className="group/btn inline-flex items-center gap-3 px-6 py-4 border border-white/20 text-white font-mono text-xs tracking-widest uppercase hover:bg-white hover:text-black transition-all duration-300 sm:whitespace-nowrap cursor-pointer"
          >
            {t('apps.viewGames')}
            <i className="ri-arrow-right-line text-base group-hover/btn:translate-x-1 transition-transform duration-200" />
          </button>
        </div>
      </RevealWrapper>

      <SiteFooter />
      <ScrollToTop />
    </div>
  );
}
